import { useState, useMemo } from 'react'
import {
  useTenantFeatures,
  useRevokeTenantFeature,
  type FeatureSource,
} from '@/hooks/useTenantFeatures'
import GrantFeatureModal from './GrantFeatureModal'

interface TenantFeatureTableProps {
  tenantId: string
  tenantName: string
}

const SOURCE_COLORS: Record<FeatureSource, { bg: string; text: string; border: string; label: string }> = {
  plan: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Plan' },
  override: { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'border-teal-500/30', label: 'Override' },
  attestation: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30', label: 'Attestation' },
  none: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/30', label: 'None' },
}

const CATEGORY_COLORS: Record<string, string> = {
  product: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  portal: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
  admin: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  distillation: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  limit: 'bg-red-500/10 text-red-400 border-red-500/30',
}

export default function TenantFeatureTable({ tenantId, tenantName }: TenantFeatureTableProps) {
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [grantingFeatureKey, setGrantingFeatureKey] = useState<string | null>(null)
  const [showGrantModal, setShowGrantModal] = useState(false)

  const { data: features, isLoading, error } = useTenantFeatures(tenantId, {
    category: categoryFilter === 'all' ? undefined : categoryFilter,
  })
  const revokeMutation = useRevokeTenantFeature()

  // Get unique categories from features
  const categories = useMemo(() => {
    if (!features) return []
    const cats = new Set(features.map((f) => f.category))
    return ['all', ...Array.from(cats).sort()]
  }, [features])

  const handleRevoke = async (featureKey: string) => {
    if (!confirm(`Revoke feature override for "${featureKey}"? The feature may still be accessible via plan or attestation.`)) {
      return
    }

    try {
      await revokeMutation.mutateAsync({ tenantId, featureKey })
    } catch (error) {
      alert('Failed to revoke feature: ' + (error as Error).message)
    }
  }

  const handleGrantClick = (featureKey?: string) => {
    setGrantingFeatureKey(featureKey || null)
    setShowGrantModal(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#FFB84D] font-mono animate-pulse">
          [LOADING FEATURE ACCESS...]
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded p-4 font-mono text-red-400">
        ERROR: Failed to load tenant features
      </div>
    )
  }

  if (!features || features.length === 0) {
    return (
      <div className="bg-[#141c24]/80 border border-[#2a3542] rounded p-8 text-center">
        <div className="text-[#8b949e] font-mono text-sm">
          No features found for this tenant
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {/* Category Filter */}
        {categories.length > 2 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`
                  px-4 py-1.5 rounded font-mono text-xs uppercase tracking-wider transition-all
                  whitespace-nowrap
                  ${categoryFilter === cat
                    ? 'bg-[#FFB84D] text-[#0a0e14] font-bold'
                    : 'bg-[#141c24] text-[#8b949e] border border-[#2a3542] hover:border-[#FFB84D]/30'
                  }
                `}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        )}

        {/* Grant Feature Button */}
        <div className="flex justify-end">
          <button
            onClick={() => handleGrantClick()}
            className="px-4 py-2 bg-[#FFB84D]/10 border border-[#FFB84D]/30 rounded
                       text-[#FFB84D] font-mono text-xs uppercase tracking-wider
                       hover:bg-[#FFB84D]/20 hover:shadow-[0_0_15px_rgba(255,184,77,0.2)]
                       transition-all duration-200"
          >
            + Grant Override
          </button>
        </div>

        {/* Features Table */}
        <div className="space-y-2">
          {features.map((feature) => (
            <div
              key={feature.key}
              className="bg-[#141c24]/80 border border-[#2a3542] rounded-sm p-4
                         hover:border-[#FFB84D]/30 transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Feature Info */}
                <div className="flex-1 min-w-0">
                  {/* Name and Badges */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <div className="text-[#e8eaed] font-semibold">
                      {feature.name}
                    </div>
                    <CategoryBadge category={feature.category} />
                    <SourceBadge source={feature.source} />
                    {feature.has_access ? (
                      <span className="text-xs text-green-400">✓</span>
                    ) : (
                      <span className="text-xs text-red-400">✗</span>
                    )}
                  </div>

                  {/* Key and Description */}
                  <div className="text-xs text-[#8b949e] font-mono mb-1">
                    [{feature.key}]
                  </div>
                  <div className="text-sm text-[#8b949e]">
                    {feature.description}
                  </div>

                  {/* Expiration */}
                  {feature.expires_at && (
                    <div className="mt-2 text-xs text-amber-400 font-mono">
                      ⏰ Expires: {new Date(feature.expires_at).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {feature.has_access && feature.source === 'override' ? (
                    <button
                      onClick={() => handleRevoke(feature.key)}
                      disabled={revokeMutation.isPending}
                      className="px-3 py-1.5 bg-[#2a3542] rounded text-xs font-mono uppercase
                                 text-[#8b949e] hover:text-red-400 hover:bg-red-500/10
                                 disabled:opacity-50 disabled:cursor-not-allowed
                                 transition-all duration-200"
                    >
                      Revoke
                    </button>
                  ) : !feature.has_access ? (
                    <button
                      onClick={() => handleGrantClick(feature.key)}
                      className="px-3 py-1.5 bg-[#2a3542] rounded text-xs font-mono uppercase
                                 text-[#8b949e] hover:text-[#FFB84D] hover:bg-[#FFB84D]/10
                                 transition-all duration-200"
                    >
                      Grant
                    </button>
                  ) : (
                    <div className="text-xs text-[#8b949e] font-mono italic">
                      Via {SOURCE_COLORS[feature.source].label.toLowerCase()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Count */}
        <div className="text-center text-xs text-[#8b949e] font-mono">
          Showing {features.length} {features.length === 1 ? 'feature' : 'features'}
          {' • '}
          {features.filter((f) => f.has_access).length} accessible
        </div>
      </div>

      {/* Grant Modal */}
      {showGrantModal && (
        <GrantFeatureModal
          tenantId={tenantId}
          tenantName={tenantName}
          preSelectedFeatureKey={grantingFeatureKey || undefined}
          onClose={() => {
            setShowGrantModal(false)
            setGrantingFeatureKey(null)
          }}
          onSuccess={() => {
            setShowGrantModal(false)
            setGrantingFeatureKey(null)
          }}
        />
      )}
    </>
  )
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function CategoryBadge({ category }: { category: string }) {
  const colorClass = CATEGORY_COLORS[category] || 'bg-gray-500/10 text-gray-400 border-gray-500/30'

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono border ${colorClass}`}>
      {category.toUpperCase()}
    </span>
  )
}

function SourceBadge({ source }: { source: FeatureSource }) {
  const { bg, text, border, label } = SOURCE_COLORS[source]

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono border ${bg} ${text} ${border}`}>
      {label}
    </span>
  )
}
